import React from 'react';
import {
  ArrowLeft,
  User,
  Users,
  MapPin,
  CreditCard,
  FileText,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  IdCard,
  Download,
  Check,
  School
} from 'lucide-react';
import { Student } from '../../types';
import { formatDate, formatCurrency } from '../../utils/helpers';

interface AdmissionDetailsViewProps {
  student: Student;
  onBack: () => void;
}

const getStatusStyles = (status: string) => {
  switch (status) {
    case 'Form_Pending':
      return 'bg-[#332711] text-[#f9daa3]';
    case 'Submitted':
      return 'bg-[#16212b] text-[#bfdbfe]';
    case 'Approved':
      return 'bg-[#173322] text-[#8ff0b8]';
    case 'Rejected':
      return 'bg-[#341a17] text-[#fbccc8]';
    default:
      return 'bg-[#28221a] text-[#b9ad9b]';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'Form_Pending':
      return 'Admission Form Pending';
    case 'Submitted':
      return 'Under Review';
    case 'Approved':
      return 'Admitted';
    case 'Rejected':
      return 'Rejected';
    default:
      return status;
  }
};

const SectionCard: React.FC<{ icon: React.ElementType; title: string; children: React.ReactNode }> = ({
  icon: Icon,
  title,
  children
}) => (
  <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
    <h3 className="text-sm font-semibold text-[#f3ede2] mb-5 flex items-center">
      <Icon className="w-4 h-4 mr-2 text-[#e2916d]" />
      {title}
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value?: React.ReactNode; fullWidth?: boolean }> = ({
  label,
  value,
  fullWidth
}) => (
  <div className={fullWidth ? 'sm:col-span-2' : ''}>
    <p className="text-xs text-[#8a7f6e] mb-1">{label}</p>
    <p className="text-sm text-[#f3ede2]">{value || value === 0 ? value : <span className="text-[#5c5346]">Not provided</span>}</p>
  </div>
);

const documentList = (student: Student) => [
  { label: 'Student Photo', url: student.photoURL },
  { label: 'Aadhar Card', url: student.aadharCardURL },
  { label: 'Birth Certificate', url: student.birthCertificateURL },
  { label: 'Transfer Certificate', url: student.transferCertificateURL },
  { label: 'Previous Marksheet', url: student.marksheetURL },
  { label: 'Medical Certificate', url: student.medicalCertificateURL },
  { label: 'Caste Certificate', url: student.casteCertificateURL },
  { label: 'Income Certificate', url: student.incomeCertificateURL }
].filter(doc => !!doc.url);

const AdmissionDetailsView: React.FC<AdmissionDetailsViewProps> = ({ student, onBack }) => {
  const documents = documentList(student);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center text-sm text-[#b9ad9b] hover:text-[#f3ede2] transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Admissions
          </button>
          <h1 className="text-2xl font-bold text-[#f3ede2]">Admission Details</h1>
          <p className="text-[#b9ad9b] mt-1">Full application submitted for {student.name}</p>
        </div>
      </div>

      {/* Identity strip */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6 flex flex-wrap items-center gap-5">
        <div className="w-16 h-16 rounded-xl bg-[#332217] flex items-center justify-center overflow-hidden flex-shrink-0">
          {student.photoURL ? (
            <img src={student.photoURL} alt={student.name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7 text-[#e2916d]" />
          )}
        </div>
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-lg font-semibold text-[#f3ede2]">{student.name}</h2>
          <p className="text-sm text-[#b9ad9b]">
            {student.studentId} &middot; {student.class}{student.section ? ` - ${student.section}` : ''} &middot; {student.gender}
          </p>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getStatusStyles(student.admissionStatus)}`}>
          {getStatusLabel(student.admissionStatus)}
        </span>
        {student.isRteQuota && (
          <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-[#241f36] text-[#d6bbfb] flex items-center gap-1.5">
            <Check className="w-3.5 h-3.5" />
            RTE 25% Quota
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard icon={User} title="Student Details">
          <Field label="Date of Birth" value={student.dateOfBirth && formatDate(student.dateOfBirth)} />
          <Field label="Aadhar Number" value={student.aadharNumber} />
          <Field label="Category" value={student.category} />
          <Field label="Caste" value={student.caste} />
          <Field label="Religion" value={student.religion} />
          <Field label="Previous School" value={student.previousSchool} />
          <Field label="PEN Number" value={student.penNumber} />
          <Field label="APAAR ID" value={student.apaarId} />
          <Field label="Medical Conditions" value={student.medicalConditions} fullWidth />
        </SectionCard>

        <SectionCard icon={Users} title="Parent Details">
          <Field label="Father's Name" value={student.fatherName} />
          <Field label="Father's Occupation" value={student.fatherOccupation} />
          <Field label="Mother's Name" value={student.motherName} />
          <Field label="Mother's Occupation" value={student.motherOccupation} />
          <Field
            label="Contact Number"
            value={student.parentContact && (
              <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-[#8a7f6e]" />{student.parentContact}</span>
            )}
          />
          <Field
            label="Email Address"
            value={student.parentEmail && (
              <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#8a7f6e]" />{student.parentEmail}</span>
            )}
          />
          <Field label="Emergency Contact" value={student.emergencyContact} />
        </SectionCard>

        <SectionCard icon={MapPin} title="Address">
          <Field label="Permanent Address" value={student.permanentAddress} fullWidth />
          <Field label="Current Address" value={student.currentAddress} fullWidth />
        </SectionCard>

        {Boolean(student.isRteQuota || student.annualIncome) && (
          <SectionCard icon={IdCard} title="RTE / EWS Details">
            <Field label="Applying under RTE Quota" value={student.isRteQuota ? 'Yes' : 'No'} />
            <Field
              label="Annual Family Income"
              value={student.annualIncome ? formatCurrency(student.annualIncome) : undefined}
            />
          </SectionCard>
        )}

        <SectionCard icon={Briefcase} title="Bank Details">
          <Field label="Account Holder Name" value={student.accountHolderName} />
          <Field label="Bank Name" value={student.bankName} />
          <Field label="Account Number" value={student.bankAccountNumber} />
          <Field label="IFSC Code" value={student.ifscCode} />
        </SectionCard>

        <SectionCard icon={CreditCard} title="Fee & Facilities">
          <Field label="Admission Fee" value={student.admissionFee ? formatCurrency(student.admissionFee) : undefined} />
          <Field label="Hostel Required" value={student.needsHostel ? 'Yes' : 'No'} />
          <Field label="Transport Required" value={student.needsTransport ? 'Yes' : 'No'} />
        </SectionCard>
      </div>

      {/* Documents */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <h3 className="text-sm font-semibold text-[#f3ede2] mb-5 flex items-center">
          <FileText className="w-4 h-4 mr-2 text-[#e2916d]" />
          Uploaded Documents
        </h3>
        {documents.length === 0 ? (
          <p className="text-sm text-[#8a7f6e]">No documents were uploaded with this application.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {documents.map(doc => (
              <a
                key={doc.label}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-3 px-4 py-3 border border-[#3a3122] rounded-lg hover:border-[#e2916d] transition-colors"
              >
                <span className="text-sm text-[#f3ede2]">{doc.label}</span>
                <Download className="w-4 h-4 text-[#8a7f6e] flex-shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
        <h3 className="text-sm font-semibold text-[#f3ede2] mb-5 flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-[#e2916d]" />
          Timeline
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Application Submitted" value={student.createdAt && formatDate(student.createdAt)} />
          <Field label="Admission Confirmed" value={student.admissionDate ? formatDate(student.admissionDate) : undefined} />
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#28221a] transition-colors text-sm"
        >
          <School className="w-4 h-4 mr-2" />
          Back to Admissions
        </button>
      </div>
    </div>
  );
};

export default AdmissionDetailsView;
